import React, {useState, useCallback} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'
import {
    Alert,
    Button,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useWishList} from '../../hooks/use-wish-list'
import {useCurrentCustomer} from '../../hooks/use-current-customer'

import GiftCertificateFields from '../../components/forms/giftCertificate-fields'
import SocialSharingIcons from '../product-view/social-sharing-icons'
import {AlertIcon, HeartIconBlack, HeartIconRed} from '../icons'

const GiftCertificateForm = ({submitForm, amount}) => {
    const {formatMessage} = useIntl()
    const [isFavourite, setIsFavourite] = useState(false)
    const form = useForm()
    const {data: customerData} = useCurrentCustomer()
    const {data: wishlist} = useWishList()
    const showToast = useToast()
    const productStyles = useMultiStyleConfig('ProductView')

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const notify = useCallback(
        (message, status) => {
            showToast({
                title: message,
                status,
                isClosable: false,
                duration: 1000
            })
        },
        [showToast]
    )

    const handleAddToWishlist = () => {
        if (!wishlist || !customerData) return

        const wishlistId = wishlist?.productWishList?.find((item) => item?.id)?.id

        const gcItemExists = wishlist?.productWishList
            ?.flatMap((list) => list?.customerProductListItems)
            ?.some((item) => item?.type === 'gift_certificate')

        if (gcItemExists) {
            notify(
                formatMessage({
                    id: 'wishlist.creation_error',
                    defaultMessage: 'Gift Card already exists in Wishlist'
                }),
                'error'
            )
            return
        }

        createCustomerProductListItem.mutate(
            {
                parameters: {
                    listId: wishlistId,
                    customerId: customerData?.customerId
                },
                body: {quantity: 1, public: false, priority: 1, type: 'gift_certificate'}
            },
            {
                onError: () => {
                    notify(
                        formatMessage({
                            id: 'wishlist.creation_error',
                            defaultMessage: 'Could not add Gift Card to Wishlist'
                        }),
                        'error'
                    )
                },
                onSuccess: () => {
                    notify(
                        formatMessage({
                            id: 'wishlist.creation_success',
                            defaultMessage: 'The product has been added to your wishlist.'
                        }),
                        'success'
                    )
                    setIsFavourite(true)
                }
            }
        )
    }

    const handleRemoveFromWishlist = () => {
        if (!wishlist || !customerData) return

        const listId = wishlist?.productWishList?.find((item) => item?.id)?.id
        const itemId = wishlist?.productWishList
            ?.flatMap((list) => list.customerProductListItems)
            .find((item) => item)?.id

        if (!itemId) return

        deleteCustomerProductListItem.mutate(
            {
                body: {},
                parameters: {customerId: customerData?.customerId, listId, itemId}
            },
            {
                onError: () => {
                    notify(
                        formatMessage({
                            id: 'wishlist.creation_error',
                            defaultMessage: 'Gift Card already removed. Add a new one.'
                        }),
                        'error'
                    )
                },
                onSuccess: () => {
                    notify(
                        formatMessage({
                            id: 'wishlist.creation_success',
                            defaultMessage: 'The product has been removed from your wishlist.'
                        }),
                        'error'
                    )
                    setIsFavourite(false)
                }
            }
        )
    }

    const handleSubmit = async (data) => {
        await submitForm(data)
        form.reset({
            receipientEmail: '',
            receipientName: '',
            senderName: '',
            senderEmail: ''
        })
    }

    return (
        <form
            onSubmit={form.handleSubmit(handleSubmit)}
            data-testid="sf-auth-modal-form-giftCertificate"
        >
            <Stack spacing={4}>
                {form.formState.errors?.global && (
                    <Alert status="error">
                        <AlertIcon color="red.500" boxSize={4} />
                        <Text fontSize="sm" ml={3}>
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}

                <GiftCertificateFields form={form} />

                <Stack spacing={6} alignItems="center" direction="row">
                    <Button
                        type="submit"
                        variant="secondaryFull"
                        width="100%"
                        isLoading={form.formState.isSubmitting}
                        isDisabled={!amount || amount < 10}
                        textTransform="capitalize"
                    >
                        {!amount || amount < 10 ? (
                            <FormattedMessage
                                defaultMessage="Select Amount"
                                id="gift_certificate.button.select_amount"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Add To Cart"
                                id="gift_certificate.button.add_to_cart"
                            />
                        )}
                    </Button>

                    <Button
                        onClick={!isFavourite ? handleAddToWishlist : handleRemoveFromWishlist}
                        variant="outline"
                        className="wishlist-button"
                        {...productStyles.wishlistButton}
                    >
                        {!isFavourite ? <HeartIconBlack /> : <HeartIconRed />}
                    </Button>
                </Stack>
                <SocialSharingIcons />
            </Stack>
        </form>
    )
}

GiftCertificateForm.propTypes = {
    submitForm: PropTypes.func,
    amount: PropTypes.number
}

export default GiftCertificateForm
